import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

import { BUTTON_THEME, Button, IconButton } from '@st-operations/uikit/src/v2'
import pretty from '@st-operations/uikit/lib/pretty'

import Header from '_components/header'
import Select from '_components/select'
import BarChart from './bar-chart'
import Card from './card'
import TableView from './table-view'
import TabView from './tab-view'

import { MOCK_PREDICTOR_VARIABLES } from './constants'
import { getPredict } from '_services/people-model'

import './styles.scss'

// TODO: fetch this (we don't have an api for it yet)
const predictorOptions = Object.keys(MOCK_PREDICTOR_VARIABLES).map((key) => ({
  value: key,
  label: pretty(key),
}))

const filterOptions = (excludeList, options) =>
  options.filter(({ value }) => !excludeList.includes(value))

const baseState = Object.fromEntries(
  Object.keys(MOCK_PREDICTOR_VARIABLES).map((pred) => [pred, { resp: false, pred: false }])
)

const getPredictorOptions = (arr) =>
  arr
    .filter((value) => !(value === null || value === 'mask'))
    .map((key) => ({
      value: key,
      label: pretty(key),
    }))

const Sidebar = ({ handleChangeVariables, variables, onPredict, loading, downloadData }) => {
  const respList = Object.entries(variables)
    .map(([key, { resp }]) => (resp ? key : null))
    .filter(Boolean)
  const predList = Object.entries(variables)
    .map(([key, { pred }]) => (pred ? key : null))
    .filter(Boolean)

  return (
    <>
      <div>
        <p>
          Response Variables <span className="people-model__resp-notice">required</span>
        </p>
        <Select
          placeholder="Select here"
          value={null}
          options={filterOptions([...respList, ...predList], predictorOptions)}
          onChange={(item) => handleChangeVariables('resp', item.value, true)}
        />
        <div style={{ marginLeft: 12, marginTop: 8 }}>
          <For each="variable" of={respList}>
            <div className="pred-label">
              {pretty(variable)}{' '}
              <IconButton
                onClick={() => handleChangeVariables('resp', variable, false)}
                aria-label="Remove response variable"
                iconProps={{
                  icon: 'delete',
                  type: 'fill',
                  width: 16,
                  height: 16,
                  color: '#333',
                }}
              />
            </div>
          </For>
        </div>
        {/* <div className="people-model__resp-variable-block">
          {Object.entries(variables).map(([variable, { resp, pred }]) => (
            <button
              className={classNames('people-model__resp-variable-button', {
                'people-model__resp-variable-button--selected': resp,
              })}
              key={variable}
              onClick={() => handleChangeVariables('resp', variable)}
            >
              {pretty(variable)}
            </button>
          ))}
        </div> */}
      </div>
      <hr />
      <div>
        <p>Predictor variables</p>
        <Select
          placeholder="Select here"
          value={null}
          options={filterOptions([...respList, ...predList], predictorOptions)}
          onChange={(item) => handleChangeVariables('pred', item.value, true)}
        />
        <div className="people-model__pred-variable-block">
          {Object.entries(variables).map(([variable, { resp, pred }]) => (
            <If condition={pred !== false}>
              <div key={variable} className="people-model__pred-variable">
                <div className="pred-label">
                  {pretty(variable)}{' '}
                  <IconButton
                    onClick={() => handleChangeVariables('pred', variable, false)}
                    aria-label="Remove predictor variable"
                    iconProps={{
                      icon: 'delete',
                      type: 'fill',
                      width: 16,
                      height: 16,
                      color: '#333',
                    }}
                  />
                </div>
                <Select
                  options={getPredictorOptions(MOCK_PREDICTOR_VARIABLES[variable])}
                  onChange={(item) => handleChangeVariables('pred', variable, item.value)}
                  // isDisabled={resp}
                />
                <If condition={resp}>
                  <span className="people-model__pred-notice">{`⚠️ Will be ignored for response "${pretty(
                    variable
                  )}"`}</span>
                </If>
              </div>
            </If>
          ))}
        </div>
      </div>
      <Button onClick={onPredict} disabled={!respList.length || loading}>
        {loading ? 'Predicting...' : 'Predict'}
      </Button>
      <a
        className={classNames('people-model__download', {
          'people-model__download--disabled': !downloadData,
        })}
        href={`data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(downloadData))}`}
        download={`people-model - ${new Date().toISOString().split('T')[0]}.json`}
      >
        Download JSON
      </a>
    </>
  )
}

const getTabs = (variables) =>
  Object.entries(variables)
    .filter(([variable, { resp, pred }]) => resp)
    .map(([variable, { resp, pred }]) => variable)

const Main = ({ variables, data }) => {
  const tabs = getTabs(variables)
  const [activeTab, setActiveTab] = useState(tabs.at(0))

  useEffect(() => {
    setActiveTab(getTabs(variables).at(0))
  }, [variables])

  return (
    <>
      <TabView options={tabs} active={activeTab} onSelect={setActiveTab} />
      <Choose>
        <When condition={data?.[activeTab]}>
          <Card
            title={`${pretty(activeTab)} graph`}
            description={'Here you can see more details of your selected variable'}
          >
            <BarChart data={data?.[activeTab]} title={activeTab} />
          </Card>
          <Card
            title={`${pretty(activeTab)} table`}
            description={'Here you can see more details of your selected variable'}
          >
            <TableView data={data?.[activeTab]} title={activeTab} />
          </Card>
        </When>
        <Otherwise>{/* TODO: zero state */}</Otherwise>
      </Choose>
    </>
  )
}

const PeopleModel = () => {
  const [variables, setVariables] = useState(baseState)
  const [data, setData] = useState(null)
  const [downloadData, setDownloadData] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleChangeVariables = (type, item, value) => {
    switch (type) {
      case 'resp':
        setVariables((prev) => ({ ...prev, [item]: { ...prev[item], resp: !prev[item].resp } }))
        break
      case 'pred':
        setVariables((prev) => ({ ...prev, [item]: { ...prev[item], pred: value } }))
        break
    }
  }

  const handlePredict = () => {
    setLoading(true)
    setDownloadData(null)

    const nullify = (val) => (val ? (val === true ? null : val) : null)
    const masks = Object.entries(variables)
      .filter(([variable, { resp, pred }]) => resp)
      .map(([variable, { resp, pred }]) => variable)

    const predictors = Object.fromEntries(
      Object.entries(variables).map(([variable, { resp, pred }]) => [variable, nullify(pred)])
    )

    const payloads = masks.map((mask) => ({ ...predictors, [mask]: 'mask' }))

    const promises = payloads.map((payload) => getPredict(payload))
    Promise.all(promises)
      .then(async (responses) => {
        const respObj = responses.reduce((acc, cur) => ({ ...acc, ...cur }), {})
        setData(respObj)
        setDownloadData({
          response_variables: { ...respObj },
          predictor_variables: Object.fromEntries(
            Object.entries(predictors).filter(([key, val]) => !!val)
          ),
        })
      })
      .catch((error) => toast.error('An error has occurred'))
      .finally(() => setLoading(false))
  }

  return (
    <div className="people-model">
      <Header
        title={
          <div className="">
            <span>People Model</span>
          </div>
        }
        actions={
          [
            // TODO: implement CRUD
            // {
            //   children: 'Exit',
            //   onClick: () => {
            //     console.log('exit')
            //   },
            //   theme: BUTTON_THEME.GHOST,
            // },
            // {
            //   children: 'Save Changes',
            //   onClick: () => {
            //     console.log('save')
            //   },
            // },
          ]
        }
      />
      <div className="people-model__layout">
        <div className="people-model__sidebar">
          <Sidebar
            variables={variables}
            handleChangeVariables={handleChangeVariables}
            onPredict={handlePredict}
            loading={loading}
            downloadData={downloadData}
          />
        </div>
        <div className="people-model__main">
          <Main variables={variables} data={data} />
        </div>
      </div>
    </div>
  )
}

export default PeopleModel
